import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { PurchaseOrderTableModel } from 'src/app/models/tables/shopping/purcharse-order-table.model';
import { PurchaseOrderDetailTableModel } from 'src/app/models/tables/shopping/purchase-order-detail-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { PurchaseOrderDetailService } from 'src/app/services/shopping/purchase-order-detail.service';
import { PurchaseOrderService } from 'src/app/services/shopping/purchase-order.service';

@Component({
  selector: 'app-purchase-order',
  templateUrl: './purchase-order.component.html',
  styleUrls: ['./purchase-order.component.scss'],
  standalone: false
})
export class PurchaseOrderComponent implements OnInit {

  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<PurchaseOrderTableModel> = new PagedResponseModel<PurchaseOrderTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  detailData: PagedResponseModel<PurchaseOrderDetailTableModel> = new PagedResponseModel<PurchaseOrderDetailTableModel>();
  nameWindow: string = 'Consulta Orden de Compra';
  projectName = ProjectName.projectName;

  activeTabIndex = 0;
  page: number = 1;
  limit: number = 10;
  columnSort: string | null = null;
  ascendant: boolean = true;
  filters: ColumnResponseModel[] = [];
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  constructor(private purchaseOrderService: PurchaseOrderService,
    private purchaseOrderDetailService: PurchaseOrderDetailService,
    private router: Router,
    private headerService: HeaderService,
    private title: Title) {

  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem('purchase-order');

    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getPurchaseOrdersForGeneralConsultation(),
    ]);
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.purchaseOrderService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Ordenes de Compra.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }

    } catch (error) {
      console.error('Error al exportar las ordenes de compra.', error);
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.purchaseOrderDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Orden de Compra Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las orden de compra detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.purchaseOrderService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.purchaseOrderDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getPurchaseOrderDetailsForDetailConsultation() {
    try {
      this.detailData = await firstValueFrom(this.purchaseOrderDetailService.getPurchaseOrderDetailsForDetailConsultation(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de las facturas.', error);
    }
  }

  async getPurchaseOrdersForGeneralConsultation() {
    try {
      this.generalData = await firstValueFrom(this.purchaseOrderService.getPurchaseOrdersForGeneralConsultation(this.page, this.limit, this.columnSort, this.ascendant, this.filters));

    } catch (error) {
      console.error('Error al buscar las ordenes de compra.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getPurchaseOrdersForGeneralConsultation()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getPurchaseOrderDetailsForDetailConsultation()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newPurchaseOrder() {
    this.router.navigate(['/purchase-order/register']);
  }

  onAction(event: { action: string, item?: PurchaseOrderTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/purchase-order/register/${event.item.purchaseOrderId}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);

    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getPurchaseOrdersForGeneralConsultation()
        break;
      case 1:
        await this.getPurchaseOrderDetailsForDetailConsultation()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
